// @ts-strict-ignore
import * as React from 'react';
import {
    AllMetricConditionOperators,
    AllObservabilityAndAnalyticsMetricTypes,
    AllUserRatingsMetricType,
    AppMonitoringHealthMetric,
    AppMonitoringHealthMetricCategory,
    AppMonitoringHealthMetricType,
    EventAndParam,
    Integration,
    IntegrationInstallationId,
    IntegrationProvider,
    IntegrationType,
    MetricConditionOperator,
    ObservabilityAndAnalyticsMetricType,
    StabilityMetricType,
    StatisticUnit,
    UserRatingsMetricType,
} from '../../types';
import Form, { FormListFieldData, Rule } from 'antd/lib/form';
import Select from 'antd/lib/select';
import { getAnalyticEventOptionsForIntegration, iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import { integrationSelectOptions } from '@/helpers/integrationSelectHelpers';
import {
    availableMetricTypesForObservabilityAndAnalyticsIntegration,
    availableMetricTypesForStabilityMonitoringIntegration,
    doesObaaMetricTypeRequireParam,
    displayStatValueWithSign,
    eventNameFromEventAndParam,
    extendedStatisticTypeForMonitoringMetric,
    isAndroidVitalsPercentageMetric,
    isMetricTypeComposed,
    isPercentageMonitoringMetric,
    separateEventAndParam,
    statTypeForMonitoringMetricType,
} from '@/helpers';
import Button from 'antd/lib/button';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import {
    DeltaMetricOperator,
    displayNameForDeltaMetricOperator,
    displayNameForMetricConditionOperator,
    ExtendedStatsByType,
    InputFieldDataWithValidation,
    symbolForMetricConditionOperator,
} from '@/typescriptHelpers';
import {
    displayFormattedMonitoringMetricValueWithSign,
    displayNameForMonitoringMetricType,
    displayObservabilityAndAnalyticsMetricsTypeAsAverage,
    displaySimplifiedNameForMonitoringMetricType,
    displayTextForMonitoringMetric,
} from '@/helpers/displayHelpers';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import InputNumber from 'antd/lib/input-number';
import Input from 'antd/lib/input/Input';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectAppIntegrations, selectMonitoringMetricsByCategory } from '@/selectors/selectors';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Tooltip from 'antd/lib/tooltip';
import MarkdownEditor from '../HOCs/MarkdownEditor';
import { sortMonitoringMetricsForDisplay } from '../monitoring/monitoringHelpers';

export type MetricOption = {
    id: string;
    name: string;
};

export interface MonitoringMetricFormValues {
    isComposedMetric: boolean;
    selectedIntegrationInstallationId?: IntegrationInstallationId;
    selectedEventId?: string;
    selectedMetricType: AppMonitoringHealthMetricType;
    thresholds: {
        selectedThreshold: number;
        operator: MetricConditionOperator;
        deltaMetricOperator?: DeltaMetricOperator;
    }[];
    composedMetrics?: Record<string, string>[];
    composeMetricsUsingEquation?: string;
    eventName?: string;
    hideOnRolloutView: boolean;
    notifyOnApproachingThreshold: boolean;
    metricDescription?: string;
    metricCustomDisplayName?: string;
}

// allow user to choose between normal or composed/calculated metric
const NormalOrComposedMetricSelector: React.FC<{
    isEdit: boolean;
    metricCategory: AppMonitoringHealthMetricCategory;
}> = ({ isEdit, metricCategory }) => {
    const form = Form.useFormInstance<MonitoringMetricFormValues>();
    const isObservabilityAndAnalyticsSection =
        metricCategory === AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics;

    // Watch for changes in isComposedMetric and reset integration ID when switching to composed
    const isComposedMetric = Form.useWatch('isComposedMetric', form);
    React.useEffect(() => {
        if (isComposedMetric === true) {
            form.setFieldValue('selectedIntegrationInstallationId', null);
        }
    }, [isComposedMetric, form]);

    return (
        <Form.Item
            name="isComposedMetric"
            hidden={!isObservabilityAndAnalyticsSection}
            rules={[{ required: true, message: 'Please select a metric type', type: 'boolean' }]}
            label="Metric type"
            required
        >
            <Select
                placeholder="Select a metric type"
                disabled={isEdit}
                style={{
                    minWidth: 200,
                }}
                onSelect={() => {
                    form.resetFields([
                        'selectedIntegrationInstallationId',
                        'selectedMetricType',
                        'selectedThreshold',
                        'selectedEventId',
                    ]);
                }}
            >
                <Select.Option value={false} key={0}>
                    Normal
                </Select.Option>
                <Select.Option value={true} key={1}>
                    Calculated from existing metrics
                </Select.Option>
            </Select>
        </Form.Item>
    );
};

type HideOnRolloutViewProps = {
    isDisabled: boolean;
};

const HideOnRolloutView: React.FC<HideOnRolloutViewProps> = ({ isDisabled }) => {
    return (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center' }}>
            <div>
                <Tooltip
                    title={
                        isDisabled ?
                            'This option is unavailable because this metric is already referenced in an automation’s settings. (This applies whether the automation is enabled or disabled.)'
                        :   ''
                    }
                >
                    <Form.Item name="hideOnRolloutView" valuePropName="checked" style={{ padding: 0, margin: 0 }}>
                        <Checkbox disabled={isDisabled}>
                            Exclude from rollout charts, release overview, and notifications
                        </Checkbox>
                    </Form.Item>
                </Tooltip>
            </div>
            <Tooltip
                title={
                    'This option is typically used when a metric is only going to be used as part of a calculated ' +
                    'metric, or for setting automation thresholds.'
                }
            >
                <InfoCircleOutlined
                    style={{
                        marginTop: 2, // minor vertical alignment fix
                        color: isDisabled ? antGrey.grey6 : runwayColors.statusGrey,
                    }}
                />
            </Tooltip>
        </div>
    );
};

// allow user to select an integration if there can be multiple. It is hidden if there is only one integration possible (unless editing) or if user selects composed metric
const IntegrationSelector: React.FC<{ integrations: Integration[]; isEdit: boolean }> = ({ integrations, isEdit }) => {
    const form = Form.useFormInstance<MonitoringMetricFormValues>();
    const isComposedMetric = Form.useWatch('isComposedMetric', form);
    const requiresIntegrationSelection = isComposedMetric === false && (isEdit || integrations.length > 1);

    return (
        <Form.Item
            hidden={!requiresIntegrationSelection}
            name="selectedIntegrationInstallationId"
            rules={
                requiresIntegrationSelection ?
                    [{ required: true, message: 'Please select an integration', type: 'string' }]
                :   []
            }
            label="Integration"
            required
        >
            <Select
                placeholder="Select an integration"
                disabled={isEdit}
                showSearch
                optionFilterProp="title"
                options={integrationSelectOptions(integrations)}
                style={{
                    minWidth: 200,
                }}
                onSelect={() => {
                    form.resetFields(['selectedMetricType', 'selectedThreshold', 'selectedEventId']);
                }}
            />
        </Form.Item>
    );
};

const metricOptionsForCategory = ({
    category,
    availableMetricTypes,
    selectedIntegration,
    eventAndParam,
    supportedMetricTypes,
    isComposedMetric,
}: {
    category: AppMonitoringHealthMetricCategory;
    availableMetricTypes: AppMonitoringHealthMetricType[];
    selectedIntegrationProvider?: IntegrationProvider;
    selectedIntegration?: Integration;
    eventAndParam?: EventAndParam;
    supportedMetricTypes?: ObservabilityAndAnalyticsMetricType[];
    isComposedMetric: boolean;
}): MetricOption[] => {
    // Special case for composed metrics
    if (isComposedMetric) {
        return AllObservabilityAndAnalyticsMetricTypes.reduce((acc, type) => {
            if (isMetricTypeComposed(type)) {
                acc.push({
                    id: type,
                    name: displayNameForMonitoringMetricType(type),
                });
            }
            return acc;
        }, []);
    }
    switch (category) {
        case AppMonitoringHealthMetricCategory.Stability:
            return availableMetricTypesForStabilityMonitoringIntegration(selectedIntegration)
                .filter((metricType) => {
                    return availableMetricTypes.includes(metricType);
                })
                .map((metricType) => {
                    return {
                        id: metricType,
                        name: displayNameForMonitoringMetricType(metricType),
                    };
                });
        case AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics: {
            const metricTypesForObaa = availableMetricTypesForObservabilityAndAnalyticsIntegration(
                selectedIntegration,
            ).filter((metricType) => {
                if (
                    supportedMetricTypes &&
                    !supportedMetricTypes.includes(metricType as ObservabilityAndAnalyticsMetricType)
                ) {
                    return false;
                }
                const needsParam = doesObaaMetricTypeRequireParam(metricType);
                const hasParam = !!eventAndParam?.paramName;
                const isParamFiltered = hasParam && eventAndParam.paramName.includes('=');
                // filtered params case gets treated like param-less (tracks a discrete value, so need metrics like per user/DAU)
                return needsParam === (hasParam && !isParamFiltered) && availableMetricTypes.includes(metricType);
            });
            return metricTypesForObaa.map((metricType) => {
                return {
                    id: metricType,
                    name: displayObservabilityAndAnalyticsMetricsTypeAsAverage(metricType, selectedIntegration?.id),
                };
            });
        }
        case AppMonitoringHealthMetricCategory.UserReviews:
            return AllUserRatingsMetricType.filter((metricType) => {
                return availableMetricTypes.includes(metricType);
            }).map((metricType) => {
                return {
                    id: metricType,
                    name: displayNameForMonitoringMetricType(metricType),
                };
            });
    }

    return [];
};

// allow user to select a metric given a chosen integration and an event name if applicable. It also shows the integration icon in its label.
const MetricAndEventSelector: React.FC<{
    metricCategory: AppMonitoringHealthMetricCategory;
    integrations: Integration[];
    availableMetricTypes: AppMonitoringHealthMetricType[];
    isEdit: boolean;
}> = ({ metricCategory, availableMetricTypes, integrations, isEdit }) => {
    const form = Form.useFormInstance<MonitoringMetricFormValues>();
    const selectedIntegrationInstallationId: IntegrationInstallationId = Form.useWatch(
        'selectedIntegrationInstallationId',
        form,
    );
    const selectedIntegration = integrations.find((i) => i.installationId === selectedIntegrationInstallationId);
    const selectedEventId = Form.useWatch('selectedEventId', form);
    const isComposedMetric = Form.useWatch('isComposedMetric', form);

    const isObservabilityAndAnalyticsSection =
        metricCategory === AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics;

    const isAndroidVitals = IntegrationType.AndroidVitals === selectedIntegration?.id;
    const shouldHideMetricTypeOptions = (!selectedIntegrationInstallationId && !isComposedMetric) || isAndroidVitals;

    const selectedMetricType = Form.useWatch('selectedMetricType', form);
    React.useEffect(() => {
        if (isAndroidVitals && !selectedMetricType) {
            form.setFieldValue('selectedMetricType', ObservabilityAndAnalyticsMetricType.GenericObaaValue);
        }
    }, [form, isAndroidVitals, selectedMetricType]);

    const [icon, iconAlt] = iconAndAltForIntegrationType(selectedIntegration?.id);

    const analyticEventOptions = selectedIntegration ? getAnalyticEventOptionsForIntegration(selectedIntegration) : [];
    const selectedEventOption = analyticEventOptions.find((option) => option.id === selectedEventId);
    const eventAndParam =
        selectedEventOption?.eventAndParam ?? (selectedEventId ? separateEventAndParam(selectedEventId) : null);

    const metricOptions = metricOptionsForCategory({
        category: metricCategory,
        availableMetricTypes,
        selectedIntegration,
        eventAndParam,
        supportedMetricTypes: selectedEventOption?.supportedMetricTypes,
        isComposedMetric,
    });

    const shouldShowEventSelector =
        isObservabilityAndAnalyticsSection && selectedIntegrationInstallationId && !isComposedMetric;

    return (
        <>
            <Form.Item
                label={
                    <Space>
                        {icon && (
                            <img
                                src={icon}
                                alt={iconAlt}
                                style={{
                                    width: 26,
                                    height: 26,
                                }}
                            />
                        )}
                        <Text
                            style={{
                                color: antGrey.grey8,
                                fontSize: 14,
                            }}
                        >
                            <>
                                Choose a metric from your <strong>{selectedIntegration?.name}</strong> integration.
                            </>
                        </Text>
                    </Space>
                }
                name="selectedEventId"
                rules={
                    shouldShowEventSelector ?
                        [
                            {
                                required: true,
                                message: 'Please select an event',
                                type: 'string',
                            },
                        ]
                    :   []
                }
                style={{
                    marginBottom: 10,
                }}
                hidden={!shouldShowEventSelector}
            >
                <Select
                    placeholder="Select an event"
                    disabled={isEdit}
                    style={{
                        minWidth: 200,
                    }}
                    onSelect={() => {
                        form.resetFields(['thresholds']);
                        form.resetFields(['selectedMetricType']);
                    }}
                >
                    {analyticEventOptions.map((option) => (
                        <Select.Option value={option.id} key={option.id}>
                            {option.name}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item
                name="selectedMetricType"
                hidden={shouldHideMetricTypeOptions}
                rules={
                    shouldHideMetricTypeOptions ?
                        []
                    :   [{ required: true, message: 'Please select a metric', type: 'string' }]
                }
            >
                <Select
                    placeholder="Select a metric"
                    disabled={isEdit || (isObservabilityAndAnalyticsSection && !selectedEventId && !isComposedMetric)}
                    style={{
                        minWidth: 200,
                    }}
                    onSelect={() => {
                        form.resetFields(['thresholds']);
                    }}
                >
                    {metricOptions.map(({ id, name }) => {
                        return (
                            <Select.Option value={id} key={id}>
                                {name}
                            </Select.Option>
                        );
                    })}
                </Select>
            </Form.Item>
        </>
    );
};

// displays a list of thresholds for a metric and a button to add a new threshold. Each threshold has a delete button.
const MetricThresholdsList: React.FC<{
    metricCategory: AppMonitoringHealthMetricCategory;
    integrations: Integration[];
    currentReleaseStatsMap?: ExtendedStatsByType;
}> = ({ metricCategory, integrations, currentReleaseStatsMap }) => {
    const form = Form.useFormInstance<MonitoringMetricFormValues>();

    const selectedEventId = Form.useWatch('selectedEventId', form);
    const selectedIntegrationInstallationId = Form.useWatch('selectedIntegrationInstallationId', form);
    const selectedMetricType = Form.useWatch('selectedMetricType', form);
    const composedFromEquation = Form.useWatch('composeMetricsUsingEquation');
    const selectedIntegration = integrations.find((i) => i.installationId === selectedIntegrationInstallationId);
    const isObservabilityAndAnalyticsSection =
        metricCategory === AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics;

    const hasSelectedMetric =
        isObservabilityAndAnalyticsSection ?
            (selectedEventId && (selectedMetricType || IntegrationType.AndroidVitals == selectedIntegration?.id)) ||
            composedFromEquation
        :   selectedMetricType;

    return (
        <Form.List name="thresholds">
            {(fields, { add, remove }) => {
                return (
                    <div
                        style={{
                            display: hasSelectedMetric ? 'flex' : 'none',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            width: '100%',
                            gap: 12,
                            position: 'relative',
                            borderRadius: 8,
                            padding: 16,
                            backgroundColor: antGrey.grey2,
                        }}
                    >
                        {fields.map((fieldData) => {
                            return (
                                <div
                                    key={fieldData.key}
                                    style={{
                                        width: '100%',
                                    }}
                                >
                                    <MetricThresholdOptionsSelector
                                        fieldData={fieldData}
                                        currentReleaseStatsMap={currentReleaseStatsMap}
                                        integrations={integrations}
                                        metricCategory={metricCategory}
                                    />
                                </div>
                            );
                        })}

                        {fields.length === 0 ?
                            <Button
                                icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                onClick={() =>
                                    add({
                                        operator: MetricConditionOperator.GreaterThan,
                                        deltaMetricOperator: DeltaMetricOperator.Above,
                                    })
                                }
                            >
                                Add threshold
                            </Button>
                        :   <Button
                                size="small"
                                danger
                                type="link"
                                icon={<DeleteOutlined />}
                                onClick={() => remove(0)}
                                style={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                }}
                            />
                        }
                    </div>
                );
            }}
        </Form.List>
    );
};

export const inputFieldDataForMonitoringMetricType: (
    metricType: AppMonitoringHealthMetricType,
    selectedIntegration?: Integration,
    eventName?: string,
) => InputFieldDataWithValidation = (metricType, selectedIntegration, eventName) => {
    const baseValidationRules: Rule[] = [{ required: true, message: 'Please enter a threshold' }];
    const data: InputFieldDataWithValidation = {};
    const additionalValidationRules: Rule[] = [];

    if (isAndroidVitalsPercentageMetric(metricType, selectedIntegration?.id, eventName)) {
        data.precision = 3;
        data.addonAfter = '%';
        data.min = 0;
        data.max = 100;
        additionalValidationRules.push(
            { max: 100, message: 'Threshold must not be greater than 100%', type: 'number' },
            { min: 0, message: 'Threshold must not be less than 0%', type: 'number' },
        );
        return {
            ...data,
            validationRules: [...baseValidationRules, ...additionalValidationRules],
        };
    }

    if (isPercentageMonitoringMetric(metricType, selectedIntegration?.id, eventName)) {
        data.addonAfter = '%';
    }

    switch (metricType) {
        case StabilityMetricType.UserStability:
        case StabilityMetricType.SessionStability:
        case StabilityMetricType.UserStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.UserStabilityDeltaFromPreviousValue:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousValue: {
            data.precision = 3;
            data.addonAfter = '%';
            data.max = 100;
            data.min = 0;

            additionalValidationRules.push(
                {
                    max: 100,
                    message: `Threshold must not be greater than 100%`,
                    type: 'number',
                },
                {
                    validator: async (rule, value) => {
                        if (value <= 0) {
                            throw new Error('Threshold must be greater than 0%');
                        }
                    },
                },
            );
            break;
        }
        case UserRatingsMetricType.AvgUserRating: {
            data.precision = 3;
            data.max = 5;
            data.min = 1;

            additionalValidationRules.push({ max: 5, message: `Threshold must not be greater than 5`, type: 'number' });
            additionalValidationRules.push({ min: 1, message: `Threshold must not be less than 1`, type: 'number' });
            break;
        }
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage: {
            data.precision = 3;
            data.max = 100;
            data.min = 0;
            data.addonAfter = '%';

            additionalValidationRules.push({
                max: 100,
                message: `Threshold must not be greater than 100%`,
                type: 'number',
            });
            additionalValidationRules.push({
                validator: async (rule, value) => {
                    if (value <= 0) {
                        throw new Error('Threshold must be greater than 0%');
                    }
                },
            });
            break;
        }
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue: {
            data.precision = 3;
            data.max = 5;
            data.min = 0;

            additionalValidationRules.push({ max: 5, message: `Threshold must not be greater than 5`, type: 'number' });
            additionalValidationRules.push({
                validator: async (rule, value) => {
                    if (value <= 0) {
                        throw new Error('Threshold must be greater than 0');
                    }
                },
            });
            break;
        }
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser:
        case ObservabilityAndAnalyticsMetricType.EventsPerSession:
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
        case ObservabilityAndAnalyticsMetricType.Composed: {
            data.precision = 3;
            data.min = 0;
            additionalValidationRules.push({
                validator: async (rule, value) => {
                    if (value <= 0) {
                        throw new Error('Threshold must be greater than 0');
                    }
                },
            });
            break;
        }
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage: {
            data.precision = 3;
            data.addonAfter = '%';
            data.min = 0;
            additionalValidationRules.push({
                validator: async (rule, value) => {
                    if (value <= 0) {
                        throw new Error('Threshold must be greater than 0');
                    }
                },
            });
            break;
        }
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue: {
            data.precision = 3;
            data.min = 0;
            additionalValidationRules.push({
                validator: async (rule, value) => {
                    if (value <= 0) {
                        throw new Error('Value must be greater than 0');
                    }
                },
            });
            break;
        }
    }

    return {
        ...data,
        validationRules: [
            ...baseValidationRules,
            ...additionalValidationRules,
            {
                min: data.min as number,
                message: `Threshold must be greater than ${data.min}`,
                type: 'number',
            },
        ],
    };
};

export const isDeltaMetric = (metricType: AppMonitoringHealthMetricType) => {
    return [
        StabilityMetricType.UserStabilityDeltaFromPreviousPercentage,
        StabilityMetricType.UserStabilityDeltaFromPreviousValue,
        StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage,
        StabilityMetricType.SessionStabilityDeltaFromPreviousValue,
        UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage,
        UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue,
    ].includes(metricType);
};

// displays the threshold option fields for a metric. The fields are different depending on the metric.
const MetricThresholdOptionsSelector: React.FC<{
    metricCategory: AppMonitoringHealthMetricCategory;
    fieldData: FormListFieldData;
    integrations: Integration[];
    currentReleaseStatsMap?: ExtendedStatsByType;
}> = ({ metricCategory, fieldData, integrations, currentReleaseStatsMap }) => {
    const form = Form.useFormInstance<MonitoringMetricFormValues>();
    const selectedMetricType = Form.useWatch('selectedMetricType', form);
    const selectedEventId = Form.useWatch('selectedEventId', form);
    const selectedIntegrationInstallationId = Form.useWatch('selectedIntegrationInstallationId', form);
    const selectedIntegration = integrations.find((i) => i.installationId === selectedIntegrationInstallationId);
    const selectedThreshold = Form.useWatch(['thresholds', fieldData.name, 'selectedThreshold'], form);
    const operator = Form.useWatch(['thresholds', fieldData.name, 'operator'], form);
    const deltaMetricOperator = Form.useWatch(['thresholds', fieldData.name, 'deltaMetricOperator'], form);
    const composedMetricName = Form.useWatch('eventName');
    const selectedEventName =
        selectedIntegration &&
        getAnalyticEventOptionsForIntegration(selectedIntegration).find((option) => option.id === selectedEventId)
            ?.name;

    const eventAndParam = selectedEventId ? separateEventAndParam(selectedEventId) : null;
    const isPercentage = isPercentageMonitoringMetric(
        selectedMetricType,
        selectedIntegration?.id,
        eventAndParam?.eventName,
    );
    const inputFieldData =
        selectedMetricType ?
            inputFieldDataForMonitoringMetricType(selectedMetricType, selectedIntegration, eventAndParam?.eventName)
        :   null;

    const conditionOperators = AllMetricConditionOperators;

    const isObservabilityAndAnalyticsSection =
        metricCategory === AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics;

    const isDelta = selectedMetricType ? isDeltaMetric(selectedMetricType) : false;

    React.useEffect(() => {
        if (isDelta && !deltaMetricOperator) {
            form.setFieldValue(['thresholds', fieldData.name, 'deltaMetricOperator'], DeltaMetricOperator.Above);
        }
    }, [isDelta, deltaMetricOperator, fieldData.name, form]);

    const statisticType =
        selectedMetricType ?
            extendedStatisticTypeForMonitoringMetric(
                selectedMetricType,
                selectedIntegration?.id,
                eventAndParam?.eventName,
            )
        :   null;

    let currentAverage = null;
    if (statisticType) {
        const currentStat = currentReleaseStatsMap?.[statisticType];
        // should this be currentStat.value or currentStat.average?
        currentAverage =
            currentStat ? displayFormattedMonitoringMetricValueWithSign(selectedMetricType, currentStat.average) : null;
    }

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text
                style={{
                    display: 'inline-block',
                    color: antGrey.grey8,
                    fontSize: 14,
                    paddingBottom: 4,
                }}
            >
                {isDelta ?
                    'Healthy if this metric is no more than...'
                : isObservabilityAndAnalyticsSection ?
                    'Healthy if this metric is...'
                :   'Healthy if this metric stays above:'}
            </Text>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    gap: 12,
                }}
            >
                <Form.Item
                    name={[fieldData.name, 'operator']}
                    hidden={!isObservabilityAndAnalyticsSection || !!isDelta}
                    rules={
                        isObservabilityAndAnalyticsSection && !isDelta ?
                            [{ required: true, message: 'Please select an operator', type: 'string' }]
                        :   []
                    }
                    style={{ marginBottom: 0 }}
                >
                    <Select
                        placeholder="Select an operator"
                        style={{
                            width: 150,
                        }}
                    >
                        {conditionOperators.map((key) => {
                            const name = displayNameForMetricConditionOperator(key as MetricConditionOperator);
                            return (
                                <Select.Option value={key} key={key}>
                                    {name}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </Form.Item>
                <Space size={24} align="start">
                    <Form.Item
                        rules={inputFieldData?.validationRules}
                        name={[fieldData.name, 'selectedThreshold']}
                        style={{ marginBottom: 0 }}
                    >
                        <InputNumber
                            placeholder="Threshold"
                            precision={inputFieldData?.precision}
                            addonAfter={inputFieldData?.addonAfter}
                            min={inputFieldData?.min}
                            max={inputFieldData?.max}
                            type="number"
                            style={{
                                width: 150,
                            }}
                        />
                    </Form.Item>
                    {!isDelta && currentAverage && (
                        <Text
                            style={{
                                display: 'inline-block',
                                color: antGrey.grey7,
                                fontSize: 14,
                                paddingTop: 4,
                            }}
                        >
                            Last 5 releases: {currentAverage}
                        </Text>
                    )}
                </Space>
                <Form.Item
                    name={[fieldData.name, 'deltaMetricOperator']}
                    hidden={!isDelta}
                    rules={isDelta ? [{ required: true, message: 'Please select above or below', type: 'string' }] : []}
                    style={{ marginBottom: 0 }}
                >
                    <Select
                        style={{
                            width: 150,
                        }}
                    >
                        {Object.values(DeltaMetricOperator).map((key) => {
                            return (
                                <Select.Option value={key} key={key}>
                                    {displayNameForDeltaMetricOperator(key as DeltaMetricOperator)}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </Form.Item>
                {isDelta && (
                    <Text
                        style={{
                            display: 'inline-block',
                            color: antGrey.grey8,
                            fontSize: 14,
                            paddingBottom: 12,
                        }}
                    >
                        {"...the previous version's value."}
                    </Text>
                )}

                <ExtendedThresholdDescription
                    threshold={{
                        selectedThreshold,
                        operator,
                        deltaMetricOperator,
                    }}
                    metricCategory={metricCategory}
                    selectedEventId={selectedEventId}
                    selectedEventName={selectedEventName}
                    metricType={selectedMetricType}
                    composedMetricName={composedMetricName}
                    unit={isPercentage ? StatisticUnit.Percent : undefined}
                />
            </div>
        </Space>
    );
};

const displayMetricNameForMetricCategory = ({
    metricCategory,
    selectedMetricType,
    selectedEventId,
    selectedEventName,
    composedMetricName,
}: {
    metricCategory: AppMonitoringHealthMetricCategory;
    selectedMetricType: AppMonitoringHealthMetricType;
    selectedEventId?: string;
    selectedEventName?: string;
    composedMetricName?: string;
}) => {
    switch (metricCategory) {
        case AppMonitoringHealthMetricCategory.Stability:
            return selectedMetricType ? displaySimplifiedNameForMonitoringMetricType(selectedMetricType) : null;

        case AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics: {
            if (selectedEventId) {
                const eventAndParam = separateEventAndParam(selectedEventId);

                return `${selectedEventName ?? eventNameFromEventAndParam(eventAndParam)} (${displaySimplifiedNameForMonitoringMetricType(selectedMetricType)})`;
            } else if (composedMetricName) {
                return composedMetricName;
            } else {
                return selectedMetricType ? displaySimplifiedNameForMonitoringMetricType(selectedMetricType) : null;
            }
        }
        case AppMonitoringHealthMetricCategory.UserReviews:
            return selectedMetricType ? displaySimplifiedNameForMonitoringMetricType(selectedMetricType) : null;
    }
};

export const ThresholdDescription: React.FC<{
    metricType: AppMonitoringHealthMetricType;
    unit?: StatisticUnit;
    threshold: {
        selectedThreshold: number;
        operator: MetricConditionOperator;
        deltaMetricOperator?: DeltaMetricOperator;
    };
}> = ({ threshold, metricType, unit }) => {
    if (!threshold || threshold.selectedThreshold === null || threshold.selectedThreshold === undefined) {
        return null;
    }

    const formattedThreshold =
        unit ?
            displayStatValueWithSign(statTypeForMonitoringMetricType(metricType), threshold.selectedThreshold, unit)
        :   displayFormattedMonitoringMetricValueWithSign(metricType, threshold.selectedThreshold);

    const isDelta = isDeltaMetric(metricType);
    return (
        <>
            <Space align="center">
                {isDelta ?
                    <Text
                        style={{
                            color: antGrey.grey8,
                        }}
                    >
                        Healthy if no more than <strong>{formattedThreshold}</strong> {threshold.deltaMetricOperator}{' '}
                        prev version
                    </Text>
                :   <Text
                        style={{
                            color: antGrey.grey8,
                        }}
                    >
                        {symbolForMetricConditionOperator(threshold.operator) ?? '>'}{' '}
                        <strong>{formattedThreshold}</strong>{' '}
                    </Text>
                }
            </Space>
        </>
    );
};

export const NUMERIC_BADGE_INDENT = 44;

export const ExtendedThresholdDescription: React.FC<{
    metricType: AppMonitoringHealthMetricType;
    metricCategory?: AppMonitoringHealthMetricCategory;
    selectedEventId?: string;
    selectedEventName?: string;
    composedMetricName?: string;
    unit?: StatisticUnit;
    threshold: {
        selectedThreshold: number;
        operator: MetricConditionOperator;
        deltaMetricOperator?: DeltaMetricOperator;
    };
    shouldIndent?: boolean;
}> = ({
    threshold,
    metricType,
    metricCategory,
    selectedEventId,
    selectedEventName,
    composedMetricName,
    unit,
    shouldIndent,
}) => {
    if (!threshold || threshold.selectedThreshold === null || threshold.selectedThreshold === undefined) {
        return null;
    }

    const formattedThreshold =
        unit ?
            displayStatValueWithSign(statTypeForMonitoringMetricType(metricType), threshold.selectedThreshold, unit)
        :   displayFormattedMonitoringMetricValueWithSign(metricType, threshold.selectedThreshold);

    const metricName =
        metricCategory &&
        displayMetricNameForMetricCategory({
            metricCategory,
            selectedMetricType: metricType,
            selectedEventId,
            selectedEventName,
            composedMetricName,
        });

    const isDelta = isDeltaMetric(metricType);
    return (
        <>
            <Space
                style={{
                    marginLeft: shouldIndent ? NUMERIC_BADGE_INDENT : 0,
                }}
            >
                {isDelta ?
                    <Text
                        style={{
                            color: antGrey.grey8,
                        }}
                    >
                        <strong>{metricName}</strong> is no more than <strong>{formattedThreshold}</strong>{' '}
                        {threshold.deltaMetricOperator} {`the previous version's value.`}
                    </Text>
                :   <Text
                        style={{
                            color: antGrey.grey8,
                        }}
                    >
                        <strong>{metricName}</strong> {symbolForMetricConditionOperator(threshold.operator) ?? '>'}{' '}
                        <strong>{formattedThreshold}</strong>.
                    </Text>
                }
            </Space>
        </>
    );
};

// displays a list of metrics available for composed metrics
const MetricsList: React.FC<{
    appId: string;
    availableMetrics: AppMonitoringHealthMetric[];
}> = ({ appId, availableMetrics }) => {
    const form = Form.useFormInstance<MonitoringMetricFormValues>();
    const isComposedMetric = Form.useWatch('isComposedMetric', form);
    const appIntegrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));

    return (
        <Form.List name="composedMetrics">
            {(fields, { add, remove }) => {
                return (
                    <div
                        style={{
                            display: isComposedMetric ? 'flex' : 'none',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            width: '100%',
                            gap: 12,
                            borderRadius: 8,
                            padding: 16,
                            backgroundColor: antGrey.grey2,
                            marginBottom: 20,
                        }}
                    >
                        {fields.map((fieldData, i) => {
                            return (
                                <div
                                    key={fieldData.key}
                                    style={{
                                        width: '100%',
                                    }}
                                >
                                    <ComposedMetricOptionsSelector
                                        index={i}
                                        fieldData={fieldData}
                                        availableMetrics={sortMonitoringMetricsForDisplay(availableMetrics)}
                                        appIntegrations={appIntegrations}
                                        onRemove={() => remove(i)}
                                    />
                                </div>
                            );
                        })}
                        <Button icon={<PlusOutlined style={{ color: runwayColors.primary }} />} onClick={() => add()}>
                            Add metric
                        </Button>
                    </div>
                );
            }}
        </Form.List>
    );
};

// displays the threshold option fields for a metric. The fields are different depending on the metric.
const ComposedMetricOptionsSelector: React.FC<{
    index: number;
    fieldData: FormListFieldData;
    availableMetrics: AppMonitoringHealthMetric[];
    appIntegrations: Integration[];
    onRemove: () => void;
}> = ({ index, fieldData, availableMetrics, appIntegrations, onRemove }) => {
    const form = Form.useFormInstance<MonitoringMetricFormValues>();

    const currentMetrics = form.getFieldValue('composedMetrics');
    const usedMetricsMap =
        currentMetrics?.reduce(
            (acc, metric) => {
                if (metric?.id) {
                    acc[metric.id] = true;
                }
                return acc;
            },
            {} as Record<string, boolean>,
        ) ?? {};

    const allowedMetricTypes = [
        ObservabilityAndAnalyticsMetricType.EventsPerActiveUser,
        ObservabilityAndAnalyticsMetricType.EventsPerSession,
        ObservabilityAndAnalyticsMetricType.AveragePropertyValues,
        ObservabilityAndAnalyticsMetricType.MedianPropertyValues,
        ObservabilityAndAnalyticsMetricType.P90PropertyValues,
        ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.P95PropertyValues,
        ObservabilityAndAnalyticsMetricType.P99PropertyValues,
        ObservabilityAndAnalyticsMetricType.GenericObaaValue,
    ];

    const validMetricsForSelection = availableMetrics.filter((metric) =>
        allowedMetricTypes.includes(metric.type as ObservabilityAndAnalyticsMetricType),
    );

    return (
        <Space
            direction="vertical"
            size={14}
            style={{
                width: '100%',
                backgroundColor: antGrey.grey2,
                borderRadius: 8,
                padding: '15px',
            }}
        >
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                }}
            >
                <AlphanumericBadge value={'M' + String(index + 1)} />
                <Form.Item
                    noStyle
                    {...fieldData}
                    rules={[
                        {
                            required: true,
                            message: 'Please select a metric',
                            type: 'string',
                        },
                    ]}
                    name={[fieldData.name, 'id']}
                >
                    <Select
                        placeholder="Select a metric"
                        optionLabelProp="label"
                        style={{
                            flexGrow: 1,
                            minWidth: 0,
                        }}
                    >
                        {validMetricsForSelection.map((metric) => {
                            const [icon, iconAlt] = iconAndAltForIntegrationType(metric.integrationId);

                            const title = displayTextForMonitoringMetric(metric, true);
                            const integration = appIntegrations.find(
                                (integration) => integration.installationId === metric.integrationInstallationIds?.[0],
                            );
                            const integrationDescription = [integration?.name, integration?.alias?.trim()]
                                .filter(Boolean)
                                .join(' · ');
                            const label = (
                                <Space align="center">
                                    {icon && <img src={icon} alt={iconAlt} width={20} height={20} />}
                                    {title}
                                </Space>
                            );

                            return (
                                <Select.Option
                                    value={metric.id}
                                    key={metric.id}
                                    label={label}
                                    disabled={usedMetricsMap[metric.id]}
                                >
                                    {label}
                                    {integrationDescription && (
                                        <div style={{ paddingLeft: 28 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {integrationDescription}
                                            </Text>
                                        </div>
                                    )}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </Form.Item>
                <Button size="small" danger type="link" icon={<DeleteOutlined />} onClick={() => onRemove()} />
            </div>
        </Space>
    );
};

const ComposedMetricEquationField: React.FC = () => {
    const form = Form.useFormInstance<MonitoringMetricFormValues>();
    const isComposedMetric = Form.useWatch('isComposedMetric', form);

    return (
        <Form.Item
            name="composeMetricsUsingEquation"
            hidden={isComposedMetric !== true}
            rules={[{ required: isComposedMetric == true, message: 'Please enter an equation', type: 'string' }]}
            label="Equation"
            required
        >
            <Input
                placeholder="Enter an equation"
                style={{
                    minWidth: 200,
                }}
            />
        </Form.Item>
    );
};

const AlphanumericBadge: React.FC<{ value: string }> = ({ value }) => {
    return (
        <div
            style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                backgroundColor: runwayColors.primary,
                color: 'white',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: 12,
                fontWeight: 500,
                flexShrink: 0,
            }}
        >
            {value}
        </div>
    );
};

const ComposedMetricNameField: React.FC<{ isEdit: boolean }> = ({ isEdit }) => {
    const form = Form.useFormInstance<MonitoringMetricFormValues>();
    const isComposedMetric = Form.useWatch('isComposedMetric', form);

    return (
        <Form.Item
            name="eventName"
            hidden={isComposedMetric !== true || isEdit}
            rules={[
                {
                    required: isComposedMetric == true,
                    message: 'Please enter a unique name for this metric',
                    type: 'string',
                },
            ]}
            label="Metric name"
            required
        >
            <Input
                placeholder="Enter a metric name"
                style={{
                    minWidth: 200,
                }}
            />
        </Form.Item>
    );
};

const ThresholdWarningOption: React.FC<{ integrations: Integration[] }> = ({ integrations }) => {
    const form = Form.useFormInstance<MonitoringMetricFormValues>();
    const selectedMetricType = Form.useWatch('selectedMetricType', form);
    const selectedIntegrationInstallationId = Form.useWatch('selectedIntegrationInstallationId', form);
    const selectedIntegration = integrations.find((i) => i.installationId === selectedIntegrationInstallationId);
    const hideOnRolloutView = Form.useWatch('hideOnRolloutView', form);
    const selectedEventId = Form.useWatch('selectedEventId', form);
    const eventName = selectedEventId ? separateEventAndParam(selectedEventId).eventName : undefined;
    const inputFieldData =
        selectedMetricType ?
            inputFieldDataForMonitoringMetricType(selectedMetricType, selectedIntegration, eventName)
        :   null;
    const maxValue = inputFieldData?.max;

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>
                <Form.Item
                    name="notifyOnApproachingThreshold"
                    valuePropName="checked"
                    style={{ padding: 0, margin: 0 }}
                >
                    <Checkbox disabled={hideOnRolloutView}>
                        Warn me when this health metric is approaching an unhealthy threshold.
                    </Checkbox>
                </Form.Item>
            </div>
            {!maxValue && (
                <Tooltip
                    title={
                        'Runway will send you a notification when you are 10% away from reaching the threshold that you selected.'
                    }
                >
                    <InfoCircleOutlined
                        style={{
                            marginTop: 2,
                            color: runwayColors.statusGrey,
                        }}
                    />
                </Tooltip>
            )}
        </div>
    );
};

const MetricCustomDisplayName: React.FC<{ metric?: AppMonitoringHealthMetric }> = ({ metric }) => {
    const generatedName =
        metric ? displayTextForMonitoringMetric({ ...metric, customDisplayName: undefined }, true) : undefined;

    return (
        <Form.Item
            name="metricCustomDisplayName"
            label="Display name"
            extra={
                <Text type="secondary">
                    {generatedName ?
                        `Replaces "${generatedName}" wherever this metric is shown.`
                    :   'Replaces the generated metric name wherever this metric is shown.'}
                </Text>
            }
        >
            <Input placeholder="Enter a display name for this metric" />
        </Form.Item>
    );
};

const METRIC_DESCRIPTION_EDITOR_HEIGHT = 160;

const MetricCustomDescription: React.FC = () => {
    return (
        <Form.Item
            name="metricDescription"
            label="Custom metric description"
            extra={<Text type="secondary">Shown in a tooltip next to the metric name. Markdown is supported.</Text>}
        >
            <MarkdownEditor
                height={METRIC_DESCRIPTION_EDITOR_HEIGHT}
                textareaProps={{ placeholder: 'Enter a description for this metric' }}
            />
        </Form.Item>
    );
};

// this component is used to display the form fields for a metric. The fields are different depending on the metric category,
export const MonitoringMetricFormFields: React.FC<{
    appId: string;
    metricCategory: AppMonitoringHealthMetricCategory;
    availableMetricTypes: AppMonitoringHealthMetricType[];
    currentReleaseStatsMap?: ExtendedStatsByType;
    integrations: Integration[];
    isEdit: boolean;
    isHideOnRolloutDisabled: boolean;
    metric?: AppMonitoringHealthMetric;
}> = ({
    appId,
    metricCategory,
    availableMetricTypes,
    currentReleaseStatsMap,
    integrations,
    isEdit,
    isHideOnRolloutDisabled,
    metric,
}) => {
    const availableMetrics = useSelector((state: RootState) =>
        selectMonitoringMetricsByCategory(state, appId, metricCategory),
    );

    return (
        <>
            <NormalOrComposedMetricSelector isEdit={isEdit} metricCategory={metricCategory} />
            <IntegrationSelector integrations={integrations} isEdit={isEdit} />
            <MetricAndEventSelector
                isEdit={isEdit}
                metricCategory={metricCategory}
                integrations={integrations}
                availableMetricTypes={availableMetricTypes}
            />
            <MetricsList appId={appId} availableMetrics={availableMetrics} />
            <ComposedMetricEquationField />
            <ComposedMetricNameField isEdit={isEdit} />
            <MetricCustomDisplayName metric={metric} />
            <MetricCustomDescription />
            <MetricThresholdsList
                currentReleaseStatsMap={currentReleaseStatsMap}
                integrations={integrations}
                metricCategory={metricCategory}
            />
            <HideOnRolloutView isDisabled={isHideOnRolloutDisabled} />
            <ThresholdWarningOption integrations={integrations} />
        </>
    );
};
